import React, { useCallback } from 'react';
import { Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import TextInput from '../shared/textInput';
import DragDrop from '../caseSubmission/dragDrop';

const useStyles = makeStyles(() => ({
  section: {
    padding: 16,
    borderRadius: 4,
  },
  subtitle: {
    margin: '1rem 0',
    fontWeight: 700,
  },
  field: {
    width: '100%',
  },
  uploadBox: {
    border: '1px dashed #ccc',
    padding: 20,
    marginTop: 16,
    textAlign: 'center',
  },
}));

type Props = {
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  uploadedFiles: any;
  setUploadedFiles: React.Dispatch<React.SetStateAction<any>>;
};

const BalanceBill: React.FC<Props> = ({
  formData,
  setFormData,
  uploadedFiles,
  setUploadedFiles,
}) => {
  const classes = useStyles();

  // Utility to update nested object by path
  const setNestedValue = (obj: any, path: string, value: any) => {
    const keys = path.split('.');
    const lastKey = keys.pop()!;
    const deepRef = keys.reduce((acc, key) => {
      if (!acc[key]) acc[key] = {};
      return acc[key];
    }, obj);
    deepRef[lastKey] = value;
  };

  const handleChange = (fieldPath: string) => (val: string) => {
    setFormData((prev: any) => {
      const updated = { ...prev };

      if (fieldPath.includes('groupid')) {
        setNestedValue(updated, fieldPath, val.toUpperCase());
      } else if (fieldPath.includes('dateOfBirth')) {
        if (val) {
          // Store in ISO format
          const formattedDate = `${val}T00:00:00Z`;
          setNestedValue(updated, fieldPath, formattedDate);
        } else {
          setNestedValue(updated, fieldPath, '');
        }
      } else {
        setNestedValue(updated, fieldPath, val);
      }

      return updated;
    });
  };

  const requiredLabel = (label: string) => (
    <>
      {label} <span style={{ color: 'red' }}>*</span>
    </>
  );

  const handleNumericInput = (e: any, length: number): void => {
    let inputValue = e.target.value.replace(/[^0-9]/g, '');
    inputValue = inputValue.slice(0, length);
    e.target.value = inputValue;
  };

  const handleFilesAccepted = useCallback((acceptedFiles: File[]) => {
    setUploadedFiles((prevFiles: any) => [...prevFiles, ...acceptedFiles]);
  }, []);

  const handleRemoveFile = (index: number) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(index, 1);
    setUploadedFiles(newFiles);
  };

  return (
    <>
      {/* Submitter Contact Info */}
      <div className={classes.section}>
        <Typography variant="subtitle1" className={classes.subtitle}>
          Submitter Contact Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextInput
              label={requiredLabel('Contact First Name')}
              value={formData.memberInfo.submitterContactInfo.firstName}
              onChange={handleChange('memberInfo.submitterContactInfo.firstName')}
              placeholder="Enter First Name"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextInput
              label={requiredLabel('Contact Last Name')}
              value={formData.memberInfo.submitterContactInfo.lastName}
              onChange={handleChange('memberInfo.submitterContactInfo.lastName')}
              placeholder="Enter Last Name"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextInput
              label={requiredLabel('Contact Company Name')}
              value={formData.memberInfo.submitterContactInfo.companyName}
              onChange={handleChange('memberInfo.submitterContactInfo.companyName')}
              placeholder="Enter Company Name"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextInput
              label={requiredLabel('Contact Email Address')}
              value={formData.memberInfo.submitterContactInfo.emailAddress}
              onChange={handleChange('memberInfo.submitterContactInfo.emailAddress')}
              type="email"
              placeholder="Enter Email Address"
              validators={['isEmail']}
              errorMessages={['Must be a valid email']}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextInput
              label={requiredLabel('Contact Phone Number')}
              value={formData.memberInfo.submitterContactInfo.phoneNumber}
              onChange={handleChange('memberInfo.submitterContactInfo.phoneNumber')}
              type="number"
              placeholder="Enter Phone"
              validators={['validatePhoneNumberLength']}
              errorMessages={['Must be a valid phone number']}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                onInput: e => handleNumericInput(e, 10),
              }}
            />
          </Grid>
        </Grid>
      </div>

      {/* Memeber Information  */}
      <div className={classes.section}>
        <Typography variant="subtitle1" className={classes.subtitle}>
          Member Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextInput
              label={requiredLabel('Member First Name')}
              placeholder="Enter First Name"
              value={formData.memberInfo.firstName}
              onChange={handleChange('memberInfo.firstName')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextInput
              label={requiredLabel('Member Last Name')}
              placeholder="Enter Last Name"
              value={formData.memberInfo.lastName}
              onChange={handleChange('memberInfo.lastName')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextInput
              label={requiredLabel('Member DOB')}
              placeholder="Enter Date"
              type="date"
              // Convert stored ISO value back to YYYY-MM-DD for the input
              value={
                formData.memberInfo.dateOfBirth ? formData.memberInfo.dateOfBirth.split('T')[0] : ''
              }
              onChange={handleChange('memberInfo.dateOfBirth')}
              validators={['required', 'isValidDOB']}
              errorMessages={['Date of birth is required', 'Must be a valid date']}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextInput
              label={requiredLabel('Member ID #')}
              placeholder="Enter Member ID#"
              value={formData.memberInfo.memberId}
              onChange={handleChange('memberInfo.memberId')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextInput
              label={requiredLabel('Member Email Address')}
              placeholder="Enter Email Address"
              type="email"
              value={formData.memberInfo.email}
              onChange={handleChange('memberInfo.email')}
              validators={['isEmail']}
              errorMessages={['Must be a valid email']}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextInput
              label={requiredLabel('Member Phone Number')}
              placeholder="Enter Phone Number"
              type="number"
              value={formData.memberInfo.phone}
              onChange={handleChange('memberInfo.phone')}
              validators={['validatePhoneNumberLength']}
              errorMessages={['Must be a valid phone number']}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                onInput: e => handleNumericInput(e, 10),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <TextInput
              label={requiredLabel('Member Mailing Address')}
              placeholder="Enter Address"
              value={formData.memberInfo.address}
              onChange={handleChange('memberInfo.address')}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextInput
              label={requiredLabel('City')}
              placeholder="Enter City"
              value={formData.memberInfo.city}
              onChange={handleChange('memberInfo.city')}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextInput
              label={requiredLabel('State')}
              placeholder="Enter State"
              value={formData.memberInfo.state}
              onChange={handleChange('memberInfo.state')}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextInput
              label={requiredLabel('Zipcode')}
              placeholder="Enter Zipcode"
              type="number"
              value={formData.memberInfo.zip}
              onChange={handleChange('memberInfo.zip')}
              validators={['validateZipCode']}
              errorMessages={['Must be a valid Zip Code']}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                maxLength: 5,
                onInput: e => handleNumericInput(e, 5),
              }}
            />
          </Grid>
        </Grid>
      </div>

      {/* Health Plan Info */}
      <div className={classes.section}>
        <Typography variant="subtitle1" className={classes.subtitle}>
          Health Plan Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextInput
              label={requiredLabel('Plan/Employer Name')}
              placeholder="Enter Plan"
              value={formData.providerInfo.description.planname}
              onChange={handleChange('providerInfo.description.planname')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextInput
              label={requiredLabel('Group #')}
              placeholder="Enter Group"
              value={formData.memberInfo.groupid}
              onChange={handleChange('memberInfo.groupid')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextInput
              label={requiredLabel('TPA Name')}
              placeholder="Enter TPA"
              value={formData.providerInfo.description.tpaName}
              onChange={handleChange('providerInfo.description.tpaName')}
            />
          </Grid>
        </Grid>
      </div>

      {/* Additional Comments */}
      <div className={classes.section}>
        <Typography variant="subtitle1" className={classes.subtitle}>
          Additional Comments
        </Typography>
        <Grid item xs={12} sm={6}>
          <TextInput
            label="Comments"
            value={formData.memberInfo.additionalComment}
            onChange={handleChange('memberInfo.additionalComment')}
            multiline
            minRow={5}
            maxRow={5}
            required={false}
          />
        </Grid>
      </div>

      {/* Upload Files */}
      <div className={classes.section}>
        <Typography variant="subtitle1" className={classes.subtitle}>
          Upload Files
        </Typography>
        <DragDrop
          upLoadedFile={uploadedFiles}
          onFilesAccepted={handleFilesAccepted}
          onRemoveFile={handleRemoveFile}
        />
      </div>

      <div className={classes.section}>
        <Typography variant="subtitle1" className={classes.subtitle}>
          For non-access issue submissions, if you have not submitted the EOB or Balance
          Bill/provider correspondence, please do so before continuing.
        </Typography>
        <Typography variant="subtitle1" className={classes.subtitle}>
          We cannot move forward in Balance Bill case negotiations without these documents.
        </Typography>
      </div>
    </>
  );
};

export default BalanceBill;
